import type { BrowserBackend } from "../backend/contracts";

export type MigrationRecoveryRecord = {
  id: string;
  appId: string;
  fromDataSchemaVersion: number;
  toDataSchemaVersion: number;
  snapshot: Uint8Array;
  snapshotSha256: string;
  createdAt: string;
  status: "prepared" | "committed" | "rolled-back";
};

export const MAX_RECOVERY_RECORDS_PER_APP = 3;

export interface MigrationRecoveryStore {
  put(record: MigrationRecoveryRecord): Promise<void>;
  get(id: string): Promise<MigrationRecoveryRecord | undefined>;
  list(): Promise<MigrationRecoveryRecord[]>;
  remove(id: string): Promise<void>;
  updateStatus(id: string, status: MigrationRecoveryRecord["status"]): Promise<void>;
}

export class MemoryMigrationRecoveryStore implements MigrationRecoveryStore {
  private readonly records = new Map<string, MigrationRecoveryRecord>();

  async put(record: MigrationRecoveryRecord): Promise<void> {
    this.records.set(record.id, structuredClone(record));
    const records = [...this.records.values()]
      .filter((value) => value.appId === record.appId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    for (const value of records.slice(MAX_RECOVERY_RECORDS_PER_APP)) {
      this.records.delete(value.id);
    }
  }

  async get(id: string): Promise<MigrationRecoveryRecord | undefined> {
    const value = this.records.get(id);
    return value ? structuredClone(value) : undefined;
  }

  async list(): Promise<MigrationRecoveryRecord[]> {
    return [...this.records.values()].map((record) => structuredClone(record));
  }

  async remove(id: string): Promise<void> {
    this.records.delete(id);
  }

  async updateStatus(id: string, status: MigrationRecoveryRecord["status"]): Promise<void> {
    const value = this.records.get(id);
    if (!value) throw new Error(`Migration recovery record ${id} was not found.`);
    this.records.set(id, { ...value, status });
  }
}

export class IndexedDbMigrationRecoveryStore implements MigrationRecoveryStore {
  private constructor(private readonly database: IDBDatabase) {}

  static async open(): Promise<IndexedDbMigrationRecoveryStore> {
    if (typeof indexedDB === "undefined") throw new Error("IndexedDB is unavailable in this browser context.");
    const database = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open("browser-local-migration-recovery", 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains("migration-recovery")) {
          request.result.createObjectStore("migration-recovery", { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Migration recovery database open failed."));
      request.onblocked = () => reject(new Error("Migration recovery database upgrade is blocked."));
    });
    return new IndexedDbMigrationRecoveryStore(database);
  }

  async put(record: MigrationRecoveryRecord): Promise<void> {
    await this.transaction("readwrite", (store) => requestToPromise(store.put(record)));
    await this.prune(record.appId);
  }

  async get(id: string): Promise<MigrationRecoveryRecord | undefined> {
    return this.transaction("readonly", (store) => requestToPromise(store.get(id)));
  }

  async list(): Promise<MigrationRecoveryRecord[]> {
    return this.transaction("readonly", (store) => requestToPromise(store.getAll()));
  }

  async remove(id: string): Promise<void> {
    await this.transaction("readwrite", (store) => requestToPromise(store.delete(id)));
  }

  async updateStatus(id: string, status: MigrationRecoveryRecord["status"]): Promise<void> {
    const record = await this.get(id);
    if (!record) throw new Error(`Migration recovery record ${id} was not found.`);
    await this.put({ ...record, status });
  }

  private async prune(appId: string): Promise<void> {
    const records = (await this.list())
      .filter((record) => record.appId === appId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    for (const record of records.slice(MAX_RECOVERY_RECORDS_PER_APP)) {
      await this.remove(record.id);
    }
  }

  private transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => Promise<T>): Promise<T> {
    const transaction = this.database.transaction("migration-recovery", mode);
    return new Promise<T>((resolve, reject) => {
      let result: T;
      let operationFinished = false;
      transaction.oncomplete = () => {
        if (operationFinished) resolve(result);
      };
      transaction.onerror = () => reject(transaction.error ?? new Error("Migration recovery transaction failed."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Migration recovery transaction aborted."));
      void operation(transaction.objectStore("migration-recovery"))
        .then((value) => {
          result = value;
          operationFinished = true;
        })
        .catch(reject);
    });
  }
}

export async function prepareMigrationRecovery(
  backend: Pick<BrowserBackend, "snapshot">,
  appId: string,
  fromDataSchemaVersion: number,
  toDataSchemaVersion: number,
): Promise<MigrationRecoveryRecord> {
  const snapshot = await backend.snapshot();
  return {
    id: crypto.randomUUID(),
    appId,
    fromDataSchemaVersion,
    toDataSchemaVersion,
    snapshot,
    snapshotSha256: await sha256Hex(snapshot),
    createdAt: new Date().toISOString(),
    status: "prepared",
  };
}

export async function restoreMigrationRecovery(
  backend: Pick<BrowserBackend, "restore">,
  record: MigrationRecoveryRecord,
): Promise<void> {
  const actual = await sha256Hex(record.snapshot);
  if (actual !== record.snapshotSha256) {
    throw new Error("Migration recovery snapshot checksum does not match.");
  }
  await backend.restore(record.snapshot);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Migration recovery request failed."));
  });
}
